// TimelineHelpers.js - Fonctions utilitaires et composants auxiliaires
import React from 'react';
import { Target, Clock } from 'lucide-react';

// Fonction pour déterminer le statut temporel d'un mois
export const getMonthStatus = (monthStr) => {
    if (monthStr === 'À planifier') return 'future';
    
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    const monthMap = {
        'Jan': 0, 'Fév': 1, 'Mar': 2, 'Avr': 3, 'Mai': 4, 'Jun': 5,
        'Jul': 6, 'Aoû': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Déc': 11
    };
    
    const [monthName, yearStr] = monthStr.split(' ');
    const month = monthMap[monthName];
    const year = parseInt(yearStr);
    
    if (year < currentYear || (year === currentYear && month < currentMonth)) {
        return 'past';
    } else if (year === currentYear && month === currentMonth) {
        return 'current';
    } else {
        return 'future';
    }
};

// Fonction pour obtenir les styles de priorité
export const getPriorityStyles = (priority) => {
    switch (priority) {
        case 'high':
            return {
                bg: 'bg-red-100',
                text: 'text-red-700',
                border: 'border-red-200',
                label: 'Haute'
            };
        case 'medium':
            return {
                bg: 'bg-yellow-100',
                text: 'text-yellow-700',
                border: 'border-yellow-200',
                label: 'Moyenne'
            };
        case 'low':
            return {
                bg: 'bg-green-100',
                text: 'text-green-700',
                border: 'border-green-200',
                label: 'Basse'
            };
        default:
            return {
                bg: 'bg-gray-100',
                text: 'text-gray-600',
                border: 'border-gray-200',
                label: 'Non définie'
            };
    }
};

// Composant pour une carte d'objectif
export const ObjectiveCard = ({ milestone }) => {
    return (
        <div className="bg-teal-50 rounded-lg border border-teal-200 p-3">
            <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <Target className="w-4 h-4 text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                            <h3 className="font-medium text-teal-900 text-sm leading-tight">
                                {milestone.title}
                            </h3>
                            <p className="text-xs text-teal-700 mt-1">
                                {milestone.objectiveName}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {milestone.value && (
                                <span className="text-xs text-teal-700 bg-teal-100 px-2 py-1 rounded">
                                    {milestone.value}
                                </span>
                            )}
                            {milestone.dueDate && (
                                <div className="flex items-center gap-1 text-xs text-teal-600">
                                    <Clock className="w-3 h-3" />
                                    {milestone.dueDate.toLocaleDateString('fr-FR', {
                                        day: '2-digit',
                                        month: '2-digit'
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Composant pour l'indicateur de mois
export const MonthIndicator = ({ month }) => {
    const monthStatus = getMonthStatus(month);
    
    const getMonthColors = (status) => {
        switch (status) {
            case 'past':
                return {
                    background: '#e57a86',
                    textColor: 'text-white',
                    secondaryTextColor: 'text-red-100'
                };
            case 'current':
                return {
                    background: '#bed1d0',
                    textColor: 'text-gray-800',
                    secondaryTextColor: 'text-gray-600'
                };
            case 'future':
                return {
                    background: '#9ca3af',
                    textColor: 'text-white',
                    secondaryTextColor: 'text-gray-200'
                };
            default:
                return {
                    background: '#016666',
                    textColor: 'text-white',
                    secondaryTextColor: 'text-teal-100'
                };
        }
    };

    const colors = getMonthColors(monthStatus);

    return (
        <div className="relative z-10 flex-shrink-0">
            <div
                className="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm"
                style={{ backgroundColor: colors.background }}
            >
                <div className="text-center">
                    {month === 'À planifier' ? (
                        <div className={`text-xs font-bold ${colors.textColor}`}>
                            À<br />plan.
                        </div>
                    ) : (
                        <>
                            <div className={`text-xs font-bold ${colors.textColor}`}>
                                {month.split(' ')[0]}
                            </div>
                            <div className={`text-xs ${colors.secondaryTextColor}`}>
                                {month.split(' ')[1]}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};